import { useQuery } from "@tanstack/react-query";
import { fetchTaxRates } from "../services/taxRateService";

/**
 * The live GST and TDS rates.
 *
 * Read fresh rather than held for the session: a rate change takes effect the
 * moment accounts makes it, and a rep quoting on a stale figure bills the
 * customer an amount the invoice will not match.
 */
export const useTaxRates = () =>
  useQuery({
    queryKey: ["tax-rates"],
    queryFn: fetchTaxRates,
    staleTime: 0,
    refetchOnMount: "always",
  });

/**
 * The GST rate as a percentage, and whether it is safe to charge yet.
 *
 * There is deliberately no default. Falling back to 18 is how a rate ends up
 * hardcoded again — silently, and only noticed once customers have been billed
 * at the wrong number. While this is loading or failed, the caller shows the
 * rate as unknown and leaves GST off rather than inventing one.
 */
export const useGstRate = () => {
  const { data, isLoading, error, refetch } = useTaxRates();
  const percent = data?.gst ?? null;
  return {
    percent,
    /* What a screen multiplies by. Zero until the real rate arrives, so an
       amount is never inflated by a guess. */
    fraction: percent == null ? 0 : percent / 100,
    ready: percent != null,
    isLoading,
    error,
    refetch,
  };
};
